// src/albumApi.js
import { 
  doc, 
  collection, 
  query, 
  where, 
  documentId,
  getDocs, 
  getDoc, 
  setDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { fetchPhotosByIds } from './utils/googlePhotosApi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { reconstructContentFromBlocks } from './utils/blockUtils';
import { entryIdForDate, startOfLocalDay, startOfNextLocalDay } from './utils/dateUtils';
import { isCustomPlaceId } from './utils/customPlacesApi';
import { isCallableRateLimitError, rateLimitRetryDelayMs, sleep } from './utils/functionRetryUtils';
import { getDriveDisplayUrl } from './utils/driveUrls';

const functions = getFunctions();
const getPhotoCaption = httpsCallable(functions, 'getPhotoCaption');
const getPlaceDetails = httpsCallable(functions, 'getPlaceDetails');

// Rate-limit wrapper for getPhotoCaption
async function safeGetPhotoCaption(params, attempt = 0) {
  try {
    return await getPhotoCaption(params);
  } catch (error) {
    if (isCallableRateLimitError(error) && attempt < 4) {
      const { limit, remaining, reset } = error.details || {};
      const waitMs = rateLimitRetryDelayMs(reset, attempt);
      console.warn(`OpenAI rate limited: ${remaining}/${limit} remaining, retrying in ${waitMs}ms`);
      await sleep(waitMs);
      return safeGetPhotoCaption(params, attempt + 1);
    }
    throw error;
  }
}

// Listen to entry metadata + blocks + photos
export function listenToEntry(albumId, entryId, callback) {
  if (!albumId || !entryId) return () => {};

  const entryRef = doc(db, 'albums', albumId, 'entries', entryId);
  const blocksRef = collection(db, 'albums', albumId, 'entries', entryId, 'blocks');
  const photosRef = collection(db, 'albums', albumId, 'entries', entryId, 'photos');

  let currentData = {
    entry: null,
    blocks: [],
    photos: [],
  };

  const unsubscribers = [];

  // Listen to entry metadata
  unsubscribers.push(
    onSnapshot(entryRef, (snap) => {
      currentData.entry = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      callback({ ...currentData });
    })
  );

  // Listen to blocks
  unsubscribers.push(
    onSnapshot(
      query(blocksRef, orderBy('timeOfDay', 'asc')),
      (snap) => {
        currentData.blocks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        callback({ ...currentData });
      }
    )
  );

  // Listen to photos
  unsubscribers.push(
    onSnapshot(
      query(photosRef, orderBy('dateTaken', 'asc')),
      (snap) => {
        currentData.photos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        callback({ ...currentData });
      }
    )
  );

  return () => unsubscribers.forEach(u => u());
}

// Add a block
export async function addBlock(albumId, entryId, timeOfDay, text, user) {
  const blocksRef = collection(db, 'albums', albumId, 'entries', entryId, 'blocks');
  
  return await addDoc(blocksRef, {
    entryId,
    authorUserId: user.uid,
    authorFirstName: user.displayName?.split(' ')[0] || 'User',
    timeOfDay, // "HHmm" format
    text,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// Update a block
export async function updateBlock(albumId, entryId, blockId, text) {
  const blockRef = doc(db, 'albums', albumId, 'entries', entryId, 'blocks', blockId);
  
  return await updateDoc(blockRef, {
    text,
    updatedAt: serverTimestamp(),
  });
}

// Delete a block
export async function deleteBlock(albumId, entryId, blockId) {
  const blockRef = doc(db, 'albums', albumId, 'entries', entryId, 'blocks', blockId);
  return await deleteDoc(blockRef);
}

// Add pending photo
export async function addPendingPhoto(albumId, entryId, photoMeta) {
  const photosRef = collection(db, 'albums', albumId, 'entries', entryId, 'photos');
  
  return await addDoc(photosRef, {
    entryId,
    blockId: null,
    localUri: photoMeta.localUri || null,
    tempId: photoMeta.tempId || null,
    filename: photoMeta.filename,
    dateTaken: photoMeta.dateTaken || serverTimestamp(),
    latitude: photoMeta.latitude ?? null,
    longitude: photoMeta.longitude ?? null,
    place: photoMeta.place ?? null,
    uploadStatus: 'PENDING',
    mimeType: photoMeta.mimeType,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// Mark photo uploaded
export async function markPhotoUploaded(albumId, entryId, photoId, remoteInfo) {
  const photoRef = doc(db, 'albums', albumId, 'entries', entryId, 'photos', photoId);
  
  return await updateDoc(photoRef, {
    remoteUrlId: remoteInfo.remoteUrlId || null,
    googlePhotoId: remoteInfo.googlePhotoId ?? null,
    driveFileId: remoteInfo.driveFileId ?? null,
    uploadStatus: 'COMPLETED',
    updatedAt: serverTimestamp(),
  });
}

// Delete photo
export async function deletePhoto(albumId, entryId, photoId) {
  const photoRef = doc(db, 'albums', albumId, 'entries', entryId, 'photos', photoId);
  return await deleteDoc(photoRef);
}

// Save or update entry metadata
export async function saveEntryMetadata(albumId, entryId, metadata, user) {
  const entriesRef = collection(db, 'albums', albumId, 'entries');
  
  const entryData = {
    title: metadata.title || '',
    mood: metadata.mood || 3,
    keywords: metadata.keywords || [],
    weather: metadata.weather || '',
    image: metadata.image || '',
    updatedAt: serverTimestamp(),
  };

  const createData = {
    ...entryData,
    createdAt: metadata.createdAt || serverTimestamp(),
    userId: user.uid,
    authorUserId: user.uid,
    authorFirstName: user.displayName?.split(' ')[0] || 'User',
    timezone: metadata.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  if (entryId) {
    const entryRef = doc(db, 'albums', albumId, 'entries', entryId);
    const entrySnap = await getDoc(entryRef);

    if (entrySnap.exists()) {
      await updateDoc(entryRef, entryData);
    } else {
      await setDoc(entryRef, createData, { merge: true });
    }

    return entryId;
  }

  const docRef = await addDoc(entriesRef, createData);
  return docRef.id;
}

// Sync blocks for an entry (add, update, delete as needed)
export async function syncBlocks(albumId, entryId, blocksToAdd, blocksToUpdate, blocksToDelete, user) {
  const operations = [];

  // Add new blocks
  for (const block of blocksToAdd) {
    operations.push(
      addBlock(albumId, entryId, block.timeOfDay, block.text, user)
    );
  }

  // Update existing blocks
  for (const block of blocksToUpdate) {
    operations.push(
      updateBlock(albumId, entryId, block.id, block.text)
    );
  }

  // Delete removed blocks
  for (const blockId of blocksToDelete) {
    operations.push(
      deleteBlock(albumId, entryId, blockId)
    );
  }

  await Promise.all(operations);
}

// Save photos to subcollection
export async function savePhotosToSubcollection(albumId, entryId, photos, user) {
  const photosRef = collection(db, 'albums', albumId, 'entries', entryId, 'photos');
  
  // Get existing photos
  const existingPhotosSnap = await getDocs(photosRef);
  const existingPhotos = existingPhotosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  
  // Create a map of existing photos by their media ID (driveFileId or googlePhotoId)
  const existingPhotoMap = new Map();
  existingPhotos.forEach(p => {
    const key = p.driveFileId || p.googlePhotoId || p.id;
    existingPhotoMap.set(key, p);
  });
  
  // Process each photo
  const operations = [];
  
  for (const photo of photos) {
    const mediaKey = photo.driveFileId || photo.id;
    const existingPhoto = existingPhotoMap.get(mediaKey);
    
    const photoData = {
      filename: photo.filename,
      mimeType: photo.mimeType,
      dateTaken: photo.date_taken || serverTimestamp(),
      latitude: photo.latitude ?? null,
      longitude: photo.longitude ?? null,
      place: photo.place ?? null,
      caption: photo.caption ?? null,
      story_caption: photo.story_caption ?? null,
      story_caption_score: photo.story_caption_score ?? null,
      uploadStatus: 'COMPLETED',
      updatedAt: serverTimestamp(),
    };
    
    // Add media IDs
    if (photo.driveFileId) {
      photoData.driveFileId = photo.driveFileId;
      photoData.googlePhotoId = null;
    } else if (photo.id) {
      photoData.googlePhotoId = photo.id;
      photoData.driveFileId = null;
    }
    
    if (existingPhoto) {
      // Update existing photo
      const photoRef = doc(db, 'albums', albumId, 'entries', entryId, 'photos', existingPhoto.id);
      operations.push(updateDoc(photoRef, photoData));
      existingPhotoMap.delete(mediaKey); // Mark as processed
    } else {
      // Add new photo
      operations.push(addDoc(photosRef, {
        ...photoData,
        entryId,
        blockId: null,
        createdAt: serverTimestamp(),
      }));
    }
  }
  
  // Delete photos that are no longer in the list
  for (const [, existingPhoto] of existingPhotoMap) {
    const photoRef = doc(db, 'albums', albumId, 'entries', entryId, 'photos', existingPhoto.id);
    operations.push(deleteDoc(photoRef));
  }
  
  await Promise.all(operations);
}

async function hydrateEntry(albumId, entryDoc, user) {
  const entry = { id: entryDoc.id, ...entryDoc.data() };

  const blocksRef = collection(db, 'albums', albumId, 'entries', entry.id, 'blocks');
  const blocksSnap = await getDocs(query(blocksRef, orderBy('timeOfDay', 'asc')));
  entry.blocks = blocksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  entry.content = entry.blocks.length > 0
    ? reconstructContentFromBlocks(entry.blocks)
    : (entry.content || '');

  const photosRef = collection(db, 'albums', albumId, 'entries', entry.id, 'photos');
  const photosSnap = await getDocs(query(photosRef, orderBy('dateTaken', 'asc')));
  const photosDocs = photosSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  entry.photos = await loadPhotoDetails(photosDocs, user, albumId, entry.id, entry.content);

  return entry;
}

async function findEntryDocByDate(albumId, date) {
  const entriesRef = collection(db, 'albums', albumId, 'entries');
  const dateEntryId = entryIdForDate(date);

  const directEntryDoc = await getDoc(doc(db, 'albums', albumId, 'entries', dateEntryId));
  if (directEntryDoc.exists()) {
    return directEntryDoc;
  }

  try {
    const entryDateSnapshot = await getDocs(query(
      entriesRef,
      where('entryDate', '==', dateEntryId),
      limit(1)
    ));
    if (!entryDateSnapshot.empty) {
      return entryDateSnapshot.docs[0];
    }

    const createdAtSnapshot = await getDocs(query(
      entriesRef,
      where('createdAt', '>=', startOfLocalDay(date)),
      where('createdAt', '<', startOfNextLocalDay(date)),
      orderBy('createdAt', 'asc'),
      limit(1)
    ));

    return createdAtSnapshot.empty ? null : createdAtSnapshot.docs[0];
  } catch (error) {
    console.warn(`Unable to query fallback entries for ${dateEntryId}`, error);
    return null;
  }
}

// Fetch entry by date (album-scoped)
export async function fetchEntryByDate(albumId, date) {
  const user = auth.currentUser;
  if (!user || !albumId) return null;

  const entryDoc = await findEntryDocByDate(albumId, date);
  return entryDoc ? hydrateEntry(albumId, entryDoc, user) : null;
}

// Helper to load photo details and generate captions if needed
async function loadPhotoDetails(photoDocs, user, albumId, entryId, entryContent) {
  const googlePhotoIds = photoDocs
    .filter(p => p.googlePhotoId && !p.driveFileId)
    .map(p => p.googlePhotoId);

  let googlePhotosApiResults = [];
  if (googlePhotoIds.length > 0) {
    googlePhotosApiResults = await fetchPhotosByIds(googlePhotoIds);
    if (!googlePhotosApiResults) {
      googlePhotosApiResults = [];
    }
  }

  // Process photos and generate captions if needed
  const processedPhotos = await Promise.all(photoDocs.map(async (photoDoc) => {
    let mergedPhoto;
    
    if (photoDoc.driveFileId) {
      // Drive file
      mergedPhoto = {
        ...photoDoc,
        baseUrl: getDriveDisplayUrl(photoDoc.driveFileId),
        date_taken: photoDoc.dateTaken?.toDate?.() || photoDoc.dateTaken,
      };
    } else if (photoDoc.googlePhotoId) {
      // Google Photos file
      const apiData = googlePhotosApiResults.find(p => p.id === photoDoc.googlePhotoId);
      if (apiData) {
        mergedPhoto = {
          ...photoDoc,
          id: photoDoc.id, // Keep Firestore doc ID
          baseUrl: apiData.baseUrl,
          productUrl: apiData.productUrl,
          mediaMetadata: apiData.mediaMetadata,
          date_taken: photoDoc.dateTaken?.toDate?.() || photoDoc.dateTaken || apiData.mediaMetadata?.creationTime,
          filename: photoDoc.filename || apiData.filename,
        };
      } else {
        console.warn(`Google Photos API data not found for ID: ${photoDoc.googlePhotoId}`);
        mergedPhoto = { ...photoDoc, date_taken: photoDoc.dateTaken?.toDate?.() || photoDoc.dateTaken };
      }
    } else {
      mergedPhoto = { ...photoDoc, date_taken: photoDoc.dateTaken?.toDate?.() || photoDoc.dateTaken };
    }

    // Generate place details if needed
    if (
      mergedPhoto.place &&
      !mergedPhoto.place.address &&
      mergedPhoto.place.place_id &&
      !isCustomPlaceId(mergedPhoto.place.place_id)
    ) {
      try {
        const result = await getPlaceDetails({ placeId: mergedPhoto.place.place_id });
        const address = result.data.address;
        mergedPhoto.place.address = address;
        
        // Update in Firestore
        if (albumId && entryId && photoDoc.id) {
          const photoRef = doc(db, 'albums', albumId, 'entries', entryId, 'photos', photoDoc.id);
          await updateDoc(photoRef, {
            place: mergedPhoto.place,
            updatedAt: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error fetching place details:', error);
      }
    }

    // Generate captions if needed
    if (mergedPhoto.baseUrl && (!mergedPhoto.caption || !mergedPhoto.story_caption || mergedPhoto.story_caption_score === undefined)) {
      try {
        const result = await safeGetPhotoCaption({
          baseUrl: mergedPhoto.baseUrl,
          entryContent: entryContent || '',
          dateTaken: mergedPhoto.date_taken,
          place: mergedPhoto.place || null,
          targetWidth: 800,
        });
        const { caption, story_caption, score } = result.data;
        
        mergedPhoto.caption = caption;
        mergedPhoto.story_caption = story_caption;
        mergedPhoto.story_caption_score = score;
        
        // Update in Firestore
        if (albumId && entryId && photoDoc.id) {
          const photoRef = doc(db, 'albums', albumId, 'entries', entryId, 'photos', photoDoc.id);
          await updateDoc(photoRef, {
            caption,
            story_caption,
            story_caption_score: score,
            updatedAt: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error generating photo captions:', error);
      }
    }

    return mergedPhoto;
  }));

  return processedPhotos;
}

// Fetch entries by dates (album-scoped)
export async function fetchEntriesByDates(albumId, dates, setFetchProgress) {
  const user = auth.currentUser;
  if (!user || !albumId) return [];

  const entries = [];
  const totalDates = dates.length;

  for (let i = 0; i < totalDates; i++) {
    const date = dates[i];
    const entry = await fetchEntryByDate(albumId, date);
    
    if (entry) {
      entries.push(entry);
    }

    if (setFetchProgress) {
      const currentProgress = Math.round(((i + 1) / totalDates) * 100);
      setFetchProgress(currentProgress);
    }
  }

  return entries;
}

// Fetch multiple entries by IDs (album-scoped)
export async function fetchMultipleEntriesByIds(albumId, entryIds, setFetchProgress) {
  const user = auth.currentUser;
  if (!user || !albumId) return [];

  const entries = [];
  const totalEntries = entryIds.length;

  for (let i = 0; i < entryIds.length; i++) {
    const entryId = entryIds[i];

    try {
      const entryDoc = await getDoc(doc(db, 'albums', albumId, 'entries', entryId));

      if (entryDoc.exists()) {
        entries.push(await hydrateEntry(albumId, entryDoc, user));
      }
    } catch (error) {
      console.error(`Error fetching entry ${entryId}:`, error);
    }

    if (setFetchProgress) {
      const currentProgress = Math.round(((i + 1) / totalEntries) * 100);
      setFetchProgress(currentProgress);
    }
  }

  return entries;
}

export async function fetchEntryDatesWithBlocks(albumId, startDate, endDateExclusive) {
  const user = auth.currentUser;
  if (!user || !albumId) return [];

  const startKey = entryIdForDate(startDate);
  const endKey = entryIdForDate(endDateExclusive);
  const entriesRef = collection(db, 'albums', albumId, 'entries');

  try {
    const entriesSnap = await getDocs(query(
      entriesRef,
      where(documentId(), '>=', startKey),
      where(documentId(), '<', endKey),
      orderBy(documentId(), 'asc')
    ));

    const dateChecks = entriesSnap.docs.map(async (entryDoc) => {
      const blocksRef = collection(db, 'albums', albumId, 'entries', entryDoc.id, 'blocks');
      const blocksSnap = await getDocs(query(blocksRef, limit(1)));
      return blocksSnap.empty ? null : entryDoc.id;
    });

    return (await Promise.all(dateChecks)).filter(Boolean);
  } catch (error) {
    console.warn(`Unable to fetch marked entry dates for album ${albumId}`, error);
    return [];
  }
}
